import * as React from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { EmailEditor } from '@react-email/editor';
import '@react-email/editor/themes/default.css';
import '@react-email/editor/styles/bubble-menu.css';
import '@react-email/editor/styles/slash-command.css';
import { get, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { EmailFields } from './components/email-fields';
import { EmailFooterNotice } from './components/email-footer-notice';
import { FileAttachments } from './components/file-attachments';
import { ForwardAttachments } from './components/forward-attachments';
import { MessageInputActions } from './components/message-input-actions';
import { ModeSelector } from './components/mode-selector';
import {
  EDITOR_EXTENSIONS,
  composeEmailBody,
  toDraftContent,
  validateEmailBody,
} from './email-editor';
import { useOutboundSubmission } from './outbound-submission';
import { createComposerSchema } from './schema';
import type { EmailEditorRef } from '@react-email/editor';
import type { FieldErrors } from 'react-hook-form';
import type { ComposerFormValues, EmailBody } from './schema';
import type { ForwardAttachment, MessageComposerProps } from './types';
import { useCan } from '~/hooks/use-can';
import { OUTBOUND_BODY_MAX_BYTES } from '@minty/shared/email';
import { Form } from '~/components/ui/form';
import { Separator } from '~/components/ui/separator';
import { useMessageSuggestion } from '~/hooks/use-message-suggestion';
import { getPostHog } from '~/lib/posthog';
import { notify } from '~/lib/toast';

const NO_FORWARD_ATTACHMENTS: Array<ForwardAttachment> = [];

function MessageComposerContent({
  mode,
  contactId = null,
  lockTo,
  onClose,
  onSent,
  originalEmail,
  forwardAttachments = NO_FORWARD_ATTACHMENTS,
  subject,
  to,
  header,
  submitLabel,
  forwardPreview,
  replyAll = false,
}: MessageComposerProps) {
  const { t } = useTranslation('mailbox');
  const suggestion = useMessageSuggestion('email', {
    contactId,
    answeringEmailId: originalEmail?.emailId ?? null,
  });
  const outboundSubmission = useOutboundSubmission();
  const editorRef = React.useRef<EmailEditorRef>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const schema = React.useMemo(
    () => createComposerSchema(t, mode, forwardAttachments),
    [t, mode, forwardAttachments],
  );
  const defaultValues = React.useMemo(
    () =>
      getDefaultValues({
        mode,
        originalEmail,
        forwardAttachments,
        subject,
        to,
        replyAll,
      }),
    [mode, originalEmail, forwardAttachments, subject, to, replyAll],
  );
  const form = useForm<ComposerFormValues>({
    resolver: zodResolver(schema),
    defaultValues,
  });
  const isSubmitting = form.formState.isSubmitting;
  const isBusy = isSubmitting || suggestion.isGenerating;
  const editorPlaceholder =
    mode === 'forward' ? t('Add a message (optional)') : t('Message body');

  function handleAttach() {
    fileInputRef.current?.click();
  }

  async function handleGenerate() {
    const editor = editorRef.current?.editor;

    if (!editor || isBusy) {
      return;
    }

    const text = await suggestion.generate(editor.getText());

    if (text !== null) {
      editorRef.current?.editor?.commands.setContent(toDraftContent(text));
    }
  }

  function handleFilesSelected(event: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(event.target.files ?? []);

    if (files.length === 0) {
      return;
    }

    form.setValue('attachments', [...form.getValues('attachments'), ...files], {
      shouldDirty: true,
      shouldValidate: true,
    });
    event.target.value = '';
  }

  async function handleValidSubmit(values: ComposerFormValues) {
    if (suggestion.isGenerating) {
      return;
    }

    const editor = editorRef.current?.editor;

    if (!editor) {
      notify.error(t('Unable to read editor content.'));
      return;
    }

    let body: EmailBody;

    try {
      body = await composeEmailBody(editor);
    } catch (error) {
      getPostHog()?.captureException(error);
      notify.error(t('Unable to read editor content.'));
      return;
    }

    const bodyResult = validateEmailBody(body, mode !== 'forward');

    if (!bodyResult.ok) {
      const { failure } = bodyResult;
      const message =
        failure.kind === 'empty'
          ? t('Add a message body.')
          : t('Message body must be {{size}} KB or smaller.', {
              size: OUTBOUND_BODY_MAX_BYTES / 1024,
            });

      form.setError('root', { message });
      return;
    }

    const outcome = await outboundSubmission.submit({
      body: bodyResult.body,
      mode,
      replyAll,
      sourceEmailId: originalEmail?.emailId ?? null,
      values,
    });

    if (!outcome.ok) {
      notify.error(outcome.message);
      return;
    }

    notify.success(
      mode === 'forward'
        ? t('Forward queued for sending.')
        : t('Message queued for sending.'),
    );
    form.reset(defaultValues);
    editorRef.current?.editor?.commands.clearContent(true);
    onSent?.();
    onClose?.();
  }

  function handleInvalidSubmit(errors: FieldErrors<ComposerFormValues>) {
    const message = ['to', 'cc', 'subject', 'attachments']
      .flatMap((field) => {
        const error = get(errors, field);

        return [
          get(error, 'message'),
          get(error, 'root.message'),
          ...(Array.isArray(error)
            ? error.map((item) => get(item, 'message'))
            : []),
        ];
      })
      .find((value): value is string => typeof value === 'string');

    const rootMessage = message ?? t('Unable to send message.');

    form.setError('root', { message: rootMessage });
  }

  return (
    <Form.Root
      form={form}
      onSubmit={handleValidSubmit}
      onInvalid={handleInvalidSubmit}
      submitMode="explicit"
      aria-busy={isBusy}
      className="flex flex-col bg-background px-4 pb-4 pt-3"
    >
      <fieldset disabled={isBusy} className="contents">
        <div className="flex flex-col gap-2 pb-2">
          {header ?? <ModeSelector mode={mode} onClose={onClose} />}
          <EmailFields mode={mode} lockTo={lockTo} />
          <Separator className="bg-border" />
        </div>

        <div className="pt-3">
          {/* The one element that grows. Past the ceiling the editor's own
              `overflow-auto` takes over, so a long draft scrolls inside the
              composer rather than pushing the feed off the screen. */}
          <div
            inert={isBusy}
            className="flex max-h-(--composer-max-height) min-h-48 overflow-hidden rounded-md border border-border bg-background"
          >
            <EmailEditor
              ref={editorRef}
              editable={!isBusy}
              className="email-composer-editor min-h-48 flex-1 overflow-auto px-4 py-3 text-sm leading-6 text-foreground outline-none"
              extensions={EDITOR_EXTENSIONS}
              placeholder={editorPlaceholder}
            />
          </div>
          {mode === 'forward' && forwardPreview}
          {/* Last, because that is where it lands on the wire — after a
              forward's quote, not between the note and it. */}
          <EmailFooterNotice />
        </div>

        {mode === 'forward' && (
          <ForwardAttachments attachments={forwardAttachments} />
        )}
        <FileAttachments />

        <input
          ref={fileInputRef}
          type="file"
          multiple
          disabled={isBusy}
          className="hidden"
          onChange={handleFilesSelected}
        />
      </fieldset>

      <div className="mt-2.5 shrink-0">
        {form.formState.errors.root?.message && (
          <p role="alert" className="mb-2 text-sm text-destructive">
            {form.formState.errors.root.message}
          </p>
        )}
        <MessageInputActions
          mode={mode}
          onAttach={handleAttach}
          onGenerate={handleGenerate}
          isBusy={isBusy}
          isGenerating={suggestion.isGenerating}
          submitLabel={submitLabel}
        />
      </div>
    </Form.Root>
  );
}

function getDefaultValues({
  mode,
  originalEmail,
  forwardAttachments,
  subject,
  to,
  replyAll,
}: Pick<MessageComposerProps, 'mode' | 'originalEmail' | 'subject' | 'to'> & {
  forwardAttachments: Array<ForwardAttachment>;
  replyAll: boolean;
}): ComposerFormValues {
  const baseSubject = subject ?? '';
  /** Whichever set it was opened with, which is the set it sends to. */
  const replyRecipients = replyAll
    ? originalEmail?.replyAll
    : originalEmail?.reply;

  return {
    to: mode === 'reply' ? (replyRecipients?.to ?? []) : to ? [to] : [],
    cc: mode === 'reply' ? (replyRecipients?.cc ?? []) : [],
    // Everything the original carried is kept until the user drops a chip.
    keptForwardAttachmentIds:
      mode === 'forward'
        ? forwardAttachments.map((attachment) => attachment.storageId)
        : [],
    subject:
      mode === 'reply' && baseSubject
        ? prefixReplySubject(baseSubject)
        : mode === 'forward' && baseSubject
          ? prefixForwardSubject(baseSubject)
          : baseSubject,
    attachments: [],
  };
}

function prefixReplySubject(subject: string) {
  return subject.startsWith('Re: ') ? subject : `Re: ${subject}`;
}

function prefixForwardSubject(subject: string) {
  return /^fwd:\s*/i.test(subject) ? subject : `Fwd: ${subject}`;
}

export function MessageComposer(
  props: Parameters<typeof MessageComposerContent>[0],
) {
  const allowed = useCan('reception');
  return allowed ? <MessageComposerContent {...props} /> : null;
}
